import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, Descriptions, Input, Space, Table, Tag, message } from "antd";
import dayjs from "dayjs";
import { PageHeader } from "../../components/PageHeader";
import { DocumentPrintSheet } from "../../components/DocumentPrintSheet";
import { QtyInput } from "../../components/QtyInput";
import { DocLink } from "../../components/DocLink";
import { ReceiveDialog } from "../../components/ReceiveDialog";
import { outstandingQty } from "../discrepancies/coverage";
import { useApi } from "../../helpers/useApi";
import { useUser } from "../../context/UserContext";
import { hasAnyGrant } from "../../helpers/permissions";
import { DT_FORMATS } from "../../helpers/constants";
import type { GINDirectEligibleLine, GoodsReceipt, PurchaseOrder, ReceiptDiscrepancy } from "../../api-models";

interface DraftLine extends GINDirectEligibleLine {
  acceptedQty: number;
  quarantinedQty: number;
  rejectedQty: number;
  unresolvedQty: number;
}

/**
 * A GIN Direct is raised against one PO and heads for one site store. Unlike a GRN the
 * destination is not chosen freely: every eligible line is pinned to the site that raised
 * the request behind it, so the server returns the line *and* its only legal store
 * (/pos/:id/gin-direct-lines) and this screen groups by that store rather than offering a
 * warehouse picker. An order covering two sites' requests is therefore delivered on two
 * documents, one per site — which is what actually happens on the ground.
 *
 * Posting it is Procurement's act, not the site's: the accepted quantity travels in this
 * delivery's own in-transit location, and the site confirms what turned up on an MRN
 * raised from this page (the same ReceiveDialog a GIN uses).
 */
export default function GINDirectDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { request, loading } = useApi();
  const { user } = useUser();

  const isNew = id === "new";
  const poId = searchParams.get("poId");
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [deliveryNoteNo, setDeliveryNoteNo] = useState("");
  const [notes, setNotes] = useState("");
  const [doc, setDoc] = useState<GoodsReceipt | null>(null);
  const [disputes, setDisputes] = useState<ReceiptDiscrepancy[]>([]);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const canReceive = hasAnyGrant(user, "MRN.Write");
  // "Nothing is eligible" and "we never found out" are different facts, and only the first
  // is a statement about the order. Without this the page answers a question it could not
  // ask — a failed lookup renders as a confident business conclusion.
  const [linesState, setLinesState] = useState<"loading" | "loaded" | "failed">("loading");

  useEffect(() => {
    if (isNew && poId) {
      request<PurchaseOrder>({ url: `/pos/${poId}` }).then(setPo).catch(() => setPo(null));
      setLinesState("loading");
      request<GINDirectEligibleLine[]>({ url: `/pos/${poId}/gin-direct-lines` })
        .then((res) => {
          setLines(
            res.map((l) => ({
              ...l,
              acceptedQty: l.outstandingQty,
              quarantinedQty: 0,
              rejectedQty: 0,
              unresolvedQty: 0,
            })),
          );
          setLinesState("loaded");
        })
        .catch(() => setLinesState("failed"));
    } else if (!isNew) {
      request<GoodsReceipt>({ url: `/gin-directs/${id}` }).then(setDoc);
      // Silent: a reader without Discrepancies.Read simply sees no dispute panel, which is
      // not an error worth a toast.
      request<ReceiptDiscrepancy[]>({ url: `/gin-directs/${id}/disputes` }, { silent: true })
        .then(setDisputes)
        .catch(() => setDisputes([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, poId]);

  // One document per destination store. Lines for other sites stay visible but are posted
  // separately, so the user can see the whole order and still record one delivery at a time.
  const stores = [...new Map(lines.map((l) => [l.siteLocationId, l])).values()];

  function updateLine(poItemId: number, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l) => (l.poItemId === poItemId ? { ...l, ...patch } : l)));
  }

  async function submit(siteLocationId: number) {
    if (!po) return;
    if (!deliveryNoteNo) {
      message.error("A supplier delivery note (GDN) reference is required.");
      return;
    }
    const items = lines
      .filter((l) => l.siteLocationId === siteLocationId)
      .filter((l) => l.acceptedQty + l.quarantinedQty + l.rejectedQty + l.unresolvedQty > 0)
      .map((l) => ({
        poItemId: l.poItemId, acceptedQty: l.acceptedQty, quarantinedQty: l.quarantinedQty,
        rejectedQty: l.rejectedQty, unresolvedQty: l.unresolvedQty,
      }));
    if (items.length === 0) {
      message.error("Enter at least one accepted, quarantined, rejected or unresolved quantity.");
      return;
    }
    const created = await request<GoodsReceipt>({
      method: "POST",
      url: "/gin-directs",
      data: { poId: po.id, locationId: siteLocationId, deliveryNoteNo, notes, items },
    });
    message.success("GIN Direct posted.");
    navigate(`/procurement/gin-directs/${created.id}`, { replace: true });
  }

  if (isNew) {
    return (
      <div>
        <PageHeader
          title="New GIN Direct"
          subtitle={po ? `${po.docNo} — ${po.supplier?.name}` : undefined}
        />
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="This issues the material to the requesting site"
          description="These lines were bought to cover a material request the Central Warehouse could not fill, so accepting them here sends them straight to that site instead of stocking CW. Record what the supplier handed over; the site confirms what actually arrived on its own receipt (MRN), and that is what fulfils the request."
        />
        <Space style={{ marginBottom: 12 }} wrap>
          <Input
            placeholder="Supplier delivery note (GDN) no. — required"
            value={deliveryNoteNo}
            onChange={(e) => setDeliveryNoteNo(e.target.value)}
            style={{ width: 300 }}
          />
          <Input placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ width: 320 }} />
        </Space>
        {linesState === "failed" && (
          <Alert
            type="error"
            showIcon
            message="Could not load this order's lines"
            description="The eligible lines could not be fetched, so this page cannot say what is receivable. Check that the server is reachable and up to date, then reload."
          />
        )}
        {linesState === "loaded" && stores.length === 0 && (
          <Card size="small">
            No line on this order can be received as a GIN Direct. Only lines bought against a
            material request, and not yet received in full, are eligible — anything else is an
            ordinary goods receipt.
          </Card>
        )}
        {stores.map((store) => (
          <Card
            key={store.siteLocationId}
            size="small"
            style={{ marginBottom: 16 }}
            title={`${store.siteName} — ${store.siteLocationName}`}
            extra={
              <Button type="primary" onClick={() => submit(store.siteLocationId)} loading={loading} disabled={!po}>
                Post GIN Direct
              </Button>
            }
          >
            <Table
              rowKey="poItemId"
              dataSource={lines.filter((l) => l.siteLocationId === store.siteLocationId)}
              pagination={false}
              columns={[
                { title: "Code", dataIndex: "itemCode" },
                { title: "Item", dataIndex: "itemName" },
                { title: "Request", render: (_, r) => <DocLink docType="MRQ" id={r.mrqId} docNo={r.mrqDocNo} /> },
                { title: "Outstanding (incl. tolerance)", render: (_, r) => `${r.outstandingQty.toFixed(3)} ${r.unitName ?? ""}` },
                {
                  title: "Accepted",
                  render: (_, r) => <QtyInput value={r.acceptedQty} unit={r.unitName} onChange={(v) => updateLine(r.poItemId, { acceptedQty: v ?? 0 })} />,
                },
                {
                  title: "Quarantined (damaged)",
                  render: (_, r) => <QtyInput value={r.quarantinedQty} unit={r.unitName} onChange={(v) => updateLine(r.poItemId, { quarantinedQty: v ?? 0 })} />,
                },
                {
                  title: "Rejected",
                  render: (_, r) => <QtyInput value={r.rejectedQty} unit={r.unitName} onChange={(v) => updateLine(r.poItemId, { rejectedQty: v ?? 0 })} />,
                },
                {
                  title: "Unresolved",
                  render: (_, r) => <QtyInput value={r.unresolvedQty} unit={r.unitName} onChange={(v) => updateLine(r.poItemId, { unresolvedQty: v ?? 0 })} />,
                },
              ]}
            />
          </Card>
        ))}
      </div>
    );
  }

  if (!doc) return null;

  type ReceiptLine = NonNullable<GoodsReceipt["items"]>[number];
  const requestOf = (r: ReceiptLine) => r.poItem?.mrqLine?.mrq;

  // Only the accepted quantity travelled, so only it is the site's to receive: quarantined
  // stock is already in GI's custody at this store, and rejected never left the supplier.
  const receivable = (doc.items ?? [])
    .map((l) => ({
      sourceLineId: l.id,
      itemCode: l.item?.code ?? String(l.itemId),
      itemName: l.item?.name ?? "",
      unit: l.item?.unit?.abbreviation,
      outstanding: l.acceptedQty - l.receivedQty,
    }))
    .filter((l) => l.outstanding > 0.0005);

  // A PO can be raised from requisition lines belonging to several requests, so the
  // request is a per-line fact. When every line happens to serve the same one, say so once
  // in the header rather than repeating it down the table.
  const requests = [...new Map((doc.items ?? []).map(requestOf).filter((m) => !!m).map((m) => [m!.id, m!])).values()];
  const soleRequest = requests.length === 1 ? requests[0] : null;

  const columns = [
    { title: "Code", render: (_: unknown, r: ReceiptLine) => r.item?.code },
    { title: "Item", render: (_: unknown, r: ReceiptLine) => r.item?.name },
    ...(soleRequest
      ? []
      : [{
          title: "Request",
          render: (_: unknown, r: ReceiptLine) => <DocLink docType="MRQ" id={requestOf(r)?.id} docNo={requestOf(r)?.docNo} fallback="-" />,
        }]),
    { title: "Accepted", dataIndex: "acceptedQty" },
    { title: "Quarantined", dataIndex: "quarantinedQty" },
    { title: "Rejected", dataIndex: "rejectedQty" },
    { title: "Unresolved", dataIndex: "unresolvedQty" },
  ];
  // The journey columns only mean something on screen — a printed delivery note is a
  // statement about what left the supplier, not about what the site has since confirmed.
  const screenColumns = [
    ...columns,
    { title: "Received at site", render: (_: unknown, r: ReceiptLine) => r.receivedQty },
    { title: "In transit", render: (_: unknown, r: ReceiptLine) => r.acceptedQty - r.receivedQty },
  ];

  return (
    <div>
      <PageHeader
        title={doc.docNo}
        subtitle={doc.supplier?.name}
        extra={
          <Space>
            {receivable.length > 0 && canReceive && (
              <Button type="primary" onClick={() => setReceiveOpen(true)}>
                Receive at site
              </Button>
            )}
            <Button onClick={() => window.print()}>Print</Button>
          </Space>
        }
      />
      {receivable.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="In transit"
          description="The accepted quantity is held in this delivery's own in-transit location until the site posts a receipt (MRN). The quantities recorded below never change."
          className="glut-no-print"
        />
      )}
      <Card size="small" style={{ marginBottom: 16 }} className="glut-no-print">
        <Descriptions column={2} size="small">
          {/* The request leads: this document exists to fulfil one, and the order and
              requisition behind it are how it came to be bought. */}
          <Descriptions.Item label="Material request">
            {soleRequest ? (
              <DocLink docType="MRQ" id={soleRequest.id} docNo={soleRequest.docNo} fallback="-" />
            ) : (
              `${requests.length} requests — see lines`
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Purchase order">
            <DocLink docType="PO" id={doc.poId} docNo={doc.po?.docNo} fallback="-" />
          </Descriptions.Item>
          {/* The requisition is the PO's — this document reaches it through the order it settles. */}
          <Descriptions.Item label="Requisition">
            <DocLink docType="PR" id={doc.po?.prId} docNo={doc.po?.pr?.docNo} fallback="-" />
          </Descriptions.Item>
          <Descriptions.Item label="Delivery note">{doc.deliveryNoteNo}</Descriptions.Item>
          <Descriptions.Item label="Delivered to">
            {doc.site?.name ? `${doc.site.name} — ${doc.location?.name ?? ""}` : (doc.location?.name ?? "-")}
          </Descriptions.Item>
          <Descriptions.Item label="Delivered">{dayjs(doc.receivedAt).format(DT_FORMATS.date)}</Descriptions.Item>
          <Descriptions.Item label="Site receipt">
            {receivable.length > 0 ? (
              <Tag color="blue">Awaiting the site&apos;s MRN</Tag>
            ) : (
              <Tag color="green">Received at site</Tag>
            )}
          </Descriptions.Item>
          {doc.notes && <Descriptions.Item label="Notes">{doc.notes}</Descriptions.Item>}
        </Descriptions>
      </Card>
      <Table rowKey="id" dataSource={doc.items ?? []} pagination={false} columns={screenColumns} className="glut-no-print" />
      <ReceiveDialog
        open={receiveOpen}
        onClose={() => {
          setReceiveOpen(false);
          request<GoodsReceipt>({ url: `/gin-directs/${id}` }).then(setDoc);
        }}
        sourceType="GIN_DIRECT"
        sourceId={doc.id}
        lines={receivable}
      />

      {disputes.length > 0 && (
        <Card size="small" title="Supplier disputes" style={{ marginTop: 16 }} className="glut-no-print">
          {/* Held against the order, not this delivery, so one shown here may have been
              opened by another delivery on the same line — the argument is the same one. */}
          <Table
            rowKey="id"
            dataSource={disputes}
            pagination={false}
            columns={[
              {
                title: "Dispute",
                render: (_, r: ReceiptDiscrepancy) => <DocLink docType="DISCREPANCY" id={r.id} docNo={`Dispute #${r.id}`} />,
              },
              { title: "Item", render: (_, r: ReceiptDiscrepancy) => (r.item ? `${r.item.code} — ${r.item.name}` : r.itemId) },
              { title: "Ordered", dataIndex: "dispatchedQty" },
              { title: "Outstanding", render: (_, r: ReceiptDiscrepancy) => outstandingQty(r) },
              {
                title: "Status",
                render: (_, r: ReceiptDiscrepancy) => <Tag color={r.status === "OPEN" ? "orange" : "green"}>{r.status}</Tag>,
              },
            ]}
          />
        </Card>
      )}

      <DocumentPrintSheet
        title="GIN Direct"
        docNo={doc.docNo}
        meta={[
          { label: "Request", value: soleRequest ? soleRequest.docNo : requests.map((m) => m.docNo).join(", ") },
          { label: "PO", value: doc.po?.docNo ?? "" },
          { label: "Supplier", value: doc.supplier?.name ?? "" },
          { label: "Delivery note", value: doc.deliveryNoteNo },
          { label: "Site", value: doc.site?.name ?? "" },
          { label: "Store", value: doc.location?.name ?? "" },
          { label: "Date", value: dayjs(doc.receivedAt).format(DT_FORMATS.date) },
        ]}
      >
        <Table rowKey="id" dataSource={doc.items ?? []} pagination={false} columns={columns} />
      </DocumentPrintSheet>
    </div>
  );
}
